import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import Checkbox from "@material-ui/core/Checkbox";
import AddIcon from "@material-ui/icons/Add";
import api, { getProfilePicUrl } from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ContactProfile360Modal from "../../components/ContactProfile360Modal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ImportContactsResultModal from "../../components/ImportContactsResultModal";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import GuideOpenButton from "../../components/GuideOpenButton";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useViewMode, ViewModeToggle } from "../../components/ViewModeToggle";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;

    if (!Array.isArray(contacts)) {
      console.warn("⚠️ LOAD_CONTACTS: Payload no es un array:", contacts);
      return state;
    }

    /** Paginación en servidor: cada respuesta sustituye la página visible (no acumular miles de filas en memoria). */
    return contacts
      .filter((c) => c && c.id)
      .map((c) => ({ ...c }));
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    
    // ✅ CORREGIR: Validar que contact existe y tiene id
    if (!contact || !contact.id) {
      console.warn("⚠️ UPDATE_CONTACTS: Contact inválido recibido:", contact);
      return state; // ✅ Retornar estado actual sin cambios
    }
    
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // ✅ Merge superficial: preserva campos sólo-en-listado (ej. tickets / última interacción).
      const updatedState = [...state];
      const oldContact = updatedState[contactIndex];
      updatedState[contactIndex] = { ...oldContact, ...contact };
      console.log("🔄 UPDATE_CONTACTS: Contacto actualizado en reducer:", {
        id: contact.id,
        name: contact.name,
        oldProfilePicUrl: oldContact?.profilePicUrl,
        newProfilePicUrl: contact.profilePicUrl,
        stateLength: updatedState.length
      });
      return updatedState;
    } else {
      console.log("➕ UPDATE_CONTACTS: Contacto nuevo agregado:", {
        id: contact.id,
        name: contact.name,
        profilePicUrl: contact.profilePicUrl
      });
      return [{ ...contact }, ...state]; // ✅ Spread para nueva referencia
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: 0,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 16,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    minWidth: 0,
  },
  contactCountBadge: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.primary.main,
    backgroundColor: theme.mode === "dark" ? "rgba(129,140,248,0.18)" : "#eef0ff",
    padding: "4px 10px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    width: "100%",
    minWidth: 0,
  },
  searchField: {
    flex: "1 1 200px",
    minWidth: 180,
    maxWidth: 280,
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 100%",
      maxWidth: "100%",
      minWidth: 0,
    },
  },
  viewToggle: {
    display: "inline-flex",
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    borderRadius: 10,
    overflow: "hidden",
    flexShrink: 0,
  },
  viewBtn: {
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 0,
    color: theme.palette.text.secondary,
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    minWidth: 0,
    flex: "1 1 280px",
    marginLeft: "auto",
    "& .MuiTextField-root": {
      minWidth: 0,
    },
  },
  contactsTable: {
    width: "100%",
    tableLayout: "fixed",
    "& th, & td": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& th:first-child, & td:first-child": {
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      paddingLeft: 8,
      paddingRight: 4,
      overflow: "visible",
      textOverflow: "clip",
    },
    "& th:last-child, & td:last-child": {
      width: 132,
      minWidth: 132,
      overflow: "visible",
    },
  },
  checkboxCell: {
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    paddingLeft: 8,
    paddingRight: 4,
    overflow: "visible",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#263244" : "#f8fafc",
    },
  },
  contactName: {
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  mutedText: {
    color: theme.palette.text.secondary,
    fontSize: "0.83rem",
  },
  avatar: {
    width: 38,
    height: 38,
    fontWeight: 800,
    color: "#ffffff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 800,
  },
  statusActive: {
    color: "#065f46",
    backgroundColor: "#d1fae5",
    border: "1px solid #a7f3d0",
  },
  statusInactive: {
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    border: "1px solid #fecaca",
  },
  actionButtons: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  rowAction: {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#dbe3ef"}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#ffffff",
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.mode === "dark" ? "rgba(96, 165, 250, 0.12)" : "#eff6ff",
      borderColor: theme.palette.primary.main,
    },
  },
  dangerAction: {
    "&:hover": {
      color: theme.palette.error.main,
      backgroundColor: theme.mode === "dark" ? "rgba(220, 38, 38, 0.14)" : "#fef2f2",
    },
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 12,
    padding: 12,
  },
  contactCard: {
    padding: 14,
    borderRadius: 14,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    backgroundColor: theme.palette.background.paper,
    minWidth: 0,
  },
  contactCardMeta: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  contactCardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    gap: 8,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  // ✅ Estados para paginación frontend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  /** Mismo Drawer que en vista ticket: perfil 360 + mismo guardado PUT profile-360. */
  const [profile360Open, setProfile360Open] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileUploadRef = useRef(null);
  const [, forceUpdate] = useState(0); // ✅ NUEVO: Estado para forzar re-render

  // Estados para selección múltiple
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionType, setActionType] = useState(""); // "delete", "export" o "import"
  const [contactCount, setContactCount] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importResultModalOpen, setImportResultModalOpen] = useState(false);
  /** Total de contactos que cumplen el filtro (servidor); no confundir con contacts.length (solo página actual). */
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useViewMode("contacts");
  const [addMenuEl, setAddMenuEl] = useState(null);
  const [moreMenuEl, setMoreMenuEl] = useState(null);

  const socketManager = useContext(SocketContext);

  const formatContactCount = (count) =>
    new Intl.NumberFormat("es-CO").format(Number(count || 0));

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setSelectedContacts([]);
    setSelectAll(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber: String(page + 1),
              limit: rowsPerPage
            }
          });

          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts || [] });
          setTotalCount(typeof data.count === "number" ? data.count : 0);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, page, rowsPerPage]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ CORREGIR: Validar que socket existe antes de agregar listeners
    if (!socket) {
      console.warn("⚠️ Socket no disponible para companyId:", companyId);
      return;
    }

    const handleContactUpdate = (data) => {
      // ✅ CORREGIR: Validar que data existe y tiene la estructura esperada
      if (!data || !data.action) {
        console.warn("⚠️ Socket contact: Data inválida recibida:", data);
        return;
      }

      if (data.action === "update" || data.action === "create") {
        // ✅ CORREGIR: Validar que data.contact existe y tiene id
        if (data.contact && data.contact.id) {
          console.log("🔄 Socket contact: Actualizando contacto:", {
            id: data.contact.id,
            name: data.contact.name,
            profilePicUrl: data.contact.profilePicUrl,
            contactData: data.contact
          });
          dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
          // ✅ NUEVO: Forzar re-render después de actualizar el estado
          forceUpdate(prev => prev + 1);
          console.log("✅ Socket contact: Contacto actualizado en tiempo real:", data.contact.name);
        } else {
          console.warn("⚠️ Socket contact: Contact inválido recibido:", data.contact);
        }
      }

      if (data.action === "delete") {
        // ✅ CORREGIR: Validar que data.contactId existe
        if (data.contactId) {
          dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
          console.log("✅ Socket contact: Contacto eliminado en tiempo real:", data.contactId);
        } else {
          console.warn("⚠️ Socket contact: contactId no encontrado en data:", data);
        }
      }
    };

    socket.on(`company-${companyId}-contact`, handleContactUpdate);

    return () => {
      // ✅ CORREGIR: Solo remover el listener específico, no desconectar el socket
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-contact`, handleContactUpdate);
      }
    };
  }, [socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setProfile360Open(false);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedContactId(null);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setProfile360Open(true);
  };

  const handleCloseProfile360 = () => {
    setProfile360Open(false);
    setSelectedContactId(null);
  };

  const handleDeleteContact = async (contactId) => {
    setLoading(true);
    try {
      await api.delete(`/contacts/${contactId}`);
      
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      setTotalCount((c) => Math.max(0, c - 1));

      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
    setDeletingContact(null);
  };

  // Funciones para selección múltiple
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
      setSelectAll(true);
    }
  };

  const handleDeleteSelectedContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    setLoading(true);
    try {
      await api.delete("/contacts/", {
        data: { contactIds: selectedContacts }
      });
      
      // ✅ CORREGIR: Actualizar el estado local después de eliminar múltiples contactos
      const n = selectedContacts.length;
      selectedContacts.forEach(contactId => {
        dispatch({ type: "DELETE_CONTACT", payload: contactId });
      });
      setTotalCount((c) => Math.max(0, c - n));

      toast.success(i18n.t("contacts.toasts.deletedAll"));
      setSelectedContacts([]);
      setSelectAll(false);
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
  };

  const handleExportSelectedContacts = () => {
    if (selectedContacts.length === 0) return;
    
    // Filtrar solo los contactos seleccionados
    const contactsToExport = contacts.filter(contact => 
      selectedContacts.includes(contact.id)
    );
    
    // Mostrar mensaje de procesamiento para grandes cantidades
    if (selectedContacts.length > 50) {
      toast.info(i18n.t("contacts.messages.processingExport").replace('{count}', selectedContacts.length));
    }
    
    // Usar setTimeout para no bloquear la UI
    setTimeout(() => {
      try {
        // Crear el CSV con los datos filtrados
        const csvData = contactsToExport.map((contact) => ({ 
          name: contact.name || '', 
          number: contact.number || '', 
          email: contact.email || '' 
        }));
        
        // Crear el contenido CSV con encoding UTF-8 BOM para Excel
        const csvHeader = 'name;number;email\n';
        const csvContent = csvData.map(row => 
          `"${row.name.replace(/"/g, '""')}";"${row.number}";"${row.email}"`
        ).join('\n');
        
        const csvString = '\ufeff' + csvHeader + csvContent; // BOM para UTF-8
        
        // Crear y descargar el archivo
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `whaticket_${selectedContacts.length}_contacts_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar la URL del blob
        URL.revokeObjectURL(url);
        
        toast.success(i18n.t("contacts.messages.exportSuccess"));
      } catch (error) {
        console.error('Error al exportar:', error);
        toast.error(i18n.t("contacts.messages.exportError"));
      }
      
      setConfirmOpen(false);
    }, 100);
  };
  
  const handleCheckAgenda = async () => {
    try {
      toast.info(i18n.t("contacts.messages.checkingAgenda"));
      
      const { data: countData } = await api.get("/contacts/count");
      if (countData && countData.success === false) {
        toast.warning(countData.message || i18n.t("contacts.messages.agendaUnavailable"));
        return;
      }

      const count = countData.count;
      
      if (count === 0) {
        toast.warning(countData.message || i18n.t("contacts.messages.agendaEmpty"));
        return;
      }
      
      setContactCount(count);
      setActionType("import");
      setConfirmOpen(true);
    } catch (err) {
      const message = err?.response?.data?.message || i18n.t("contacts.messages.agendaUnavailable");
      toast.warning(message);
    }
  };

  const handleimportContact = async () => {
    try {
      setLoading(true);
      setConfirmOpen(false);
      
      if (!!fileUploadRef.current.files[0]) {
        const formData = new FormData();
        formData.append("file", fileUploadRef.current.files[0]);
        
        // ✅ CORREGIR: Capturar la respuesta del backend con mejor manejo de errores
        const response = await api.request({
          url: `/contacts/upload`,
          method: "POST",
          data: formData,
        });
        
        // ✅ CORREGIR: Validar que la respuesta existe y tiene la estructura esperada
        if (response && response.data) {
          if (response.data.success) {
            const { message } = response.data; // ✅ CORREGIR: Solo usar message, no importedContacts
            toast.success(`✅ ${message}`);
            
            // ✅ CORREGIDO: No hacer RESET porque los sockets ya actualizaron el estado en tiempo real
            // Los contactos ya están visibles gracias a las actualizaciones de socket
          } else {
            toast.error(response.data.message || i18n.t("contacts.messages.importErrorGeneric"));
          }
        } else {
          toast.error(i18n.t("contacts.messages.importInvalidResponse"));
        }
      } else {
        // ✅ Importar los contactos desde la agenda de WhatsApp
        const response = await api.post("/contacts/import");
        
        // ✅ Mostrar modal con resultados detallados (sin toasts)
        if (response && response.data) {
          setImportResult(response.data);
          setImportResultModalOpen(true);
        }
      }
      
      // ✅ Limpiar estados
      setContactCount(0);
      setActionType("");
      
      // ✅ CORREGIR: No usar history.go(0) que recarga toda la página
      // Los contactos se actualizarán automáticamente vía sockets
    } catch (err) {
      console.error("Error en importación de contactos:", err);
      const message = err?.response?.data?.message || i18n.t("contacts.messages.agendaUnavailable");
      toast.warning(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // ✅ CORREGIR: Usar la URL completa del backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const fileUrl = `${backendUrl}/files/plantilla-watoolx-contact.xlsx`;
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'plantilla-watoolx-contact.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(i18n.t("contacts.messages.templateDownloadSuccess"));
    } catch (error) {
      console.error('Error al descargar plantilla:', error);
      toast.error(i18n.t("contacts.messages.templateDownloadError"));
    }
  };
  
function getDateLastMessage(contact) {
    if (!contact) return null;
    if (!contact.tickets) return null;

    if (contact.tickets.length > 0) {
        const date = new Date(contact.tickets[contact.tickets.length - 1].updatedAt);

        const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
        const month = (date.getMonth() + 1) > 9 ? (date.getMonth() + 1) : `0${date.getMonth() + 1}`;
        const year = date.getFullYear().toString().slice(-2);

        const hours = date.getHours() > 9 ? date.getHours() : `0${date.getHours()}`;
        const minutes = date.getMinutes() > 9 ? date.getMinutes() : `0${date.getMinutes()}`;

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return null;
}

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactProfile360Modal
        open={profile360Open}
        onClose={handleCloseProfile360}
        contactId={selectedContactId || undefined}
        onContactUpdated={async () => {
          if (!selectedContactId) return;
          try {
            const { data } = await api.get(`/contacts/${selectedContactId}`, {
              params: { _t: Date.now(), _nocache: true }
            });
            if (data && data.id) {
              dispatch({ type: "UPDATE_CONTACTS", payload: data });
              forceUpdate((n) => n + 1);
            }
          } catch (_) {
            /* noop */
          }
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
        onSave={(saved) => {
          if (!saved || typeof saved !== "object" || !saved.id) return;
          dispatch({ type: "UPDATE_CONTACTS", payload: saved });
          forceUpdate((n) => n + 1);
        }}
      />
      <ImportContactsResultModal
        open={importResultModalOpen}
        onClose={() => {
          setImportResultModalOpen(false);
          setImportResult(null);
        }}
        result={importResult}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : actionType === "import"
            ? `${i18n.t("contacts.confirmationModal.importTitle")}`
            : actionType === "export" && selectedContacts.length > 0
            ? selectedContacts.length === contacts.length && contacts.length >= totalCount && totalCount > 0
              ? i18n.t("contacts.confirmationModal.titleExportAll")
              : selectedContacts.length === 1
              ? i18n.t("contacts.confirmationModal.titleExportOne")
              : i18n.t("contacts.confirmationModal.titleExportMany", {
                  count: selectedContacts.length,
                })
            : selectedContacts.length > 0
            ? selectedContacts.length === contacts.length && contacts.length >= totalCount && totalCount > 0
              ? `${i18n.t("contacts.confirmationModal.deleteAllTitle")}`
              : selectedContacts.length === 1
              ? i18n.t("contacts.confirmationModal.titleDeleteOne")
              : i18n.t("contacts.confirmationModal.titleDeleteMany", {
                  count: selectedContacts.length,
                })
            : `${i18n.t("contacts.confirmationModal.importTitle")}`
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setActionType("");
          setContactCount(0);
        }}
        onConfirm={(e) => {
          if (deletingContact) {
            handleDeleteContact(deletingContact.id);
          } else if (actionType === "import") {
            // ✅ CORREGIR: Priorizar importación cuando actionType es "import"
            // Esto puede ser desde archivo (fileUploadRef) o desde agenda (contactCount > 0)
            handleimportContact();
          } else if (actionType === "export" && selectedContacts.length > 0) {
            handleExportSelectedContacts();
          } else if (selectedContacts.length > 0) {
            // ✅ Solo borrar si NO hay acción de importación pendiente
            handleDeleteSelectedContacts();
          } else {
            handleimportContact();
          }
        }}
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : actionType === "import"
          ? contactCount > 0
            ? i18n.t("contacts.confirmationModal.importAgendaMessage").replace('{count}', contactCount)
            : fileUploadRef.current?.files?.[0]
            ? `${i18n.t("contacts.confirmationModal.importMessage")} ${fileUploadRef.current.files[0].name}`
            : `${i18n.t("contacts.confirmationModal.importMessage")}`
          : actionType === "export" && selectedContacts.length > 0
          ? selectedContacts.length === contacts.length && contacts.length >= totalCount && totalCount > 0
            ? i18n.t("contacts.confirmationModal.exportConfirmAll")
            : i18n.t("contacts.confirmationModal.exportConfirmMany", {
                count: selectedContacts.length,
              })
          : selectedContacts.length > 0
          ? selectedContacts.length === contacts.length && contacts.length >= totalCount && totalCount > 0
            ? `${i18n.t("contacts.confirmationModal.deleteAllMessage")}`
            : i18n.t("contacts.confirmationModal.deleteSelectedMany", {
                count: selectedContacts.length,
              })
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <MainHeader>
        <Title subtitle="Gestiona los contactos de tu cuenta, sus etiquetas y conversaciones.">
          <span className={classes.titleContent}>
            <span>{i18n.t("contacts.title")}</span>
            <span className={classes.contactCountBadge}>
              {formatContactCount(totalCount)}{" "}
              {totalCount === 1
                ? i18n.t("contacts.badge.contact")
                : i18n.t("contacts.badge.contacts")}
            </span>
          </span>
        </Title>
        <div className={classes.headerActions}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            size="small"
            variant="outlined"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <GuideOpenButton id="contacts" title={i18n.t("contacts.title")} to="/contacts" startId="list" />
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => setAddMenuEl(e.currentTarget)}
            startIcon={<AddIcon />}
            endIcon={<ExpandMoreIcon />}
            style={{ textTransform: "none", fontWeight: 700, borderRadius: 10 }}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>
          <Menu
            anchorEl={addMenuEl}
            open={Boolean(addMenuEl)}
            onClose={() => setAddMenuEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                handleOpenContactModal();
              }}
            >
              {i18n.t("contacts.buttons.add")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                handleCheckAgenda();
              }}
            >
              {i18n.t("contacts.buttons.import")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                setSelectedContacts([]);
                setSelectAll(false);
                fileUploadRef.current.value = null;
                fileUploadRef.current.click();
              }}
            >
              {i18n.t("contacts.buttons.importSheet")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAddMenuEl(null);
                handleDownloadTemplate();
              }}
            >
              {i18n.t("contacts.buttons.downloadTemplate")}
            </MenuItem>
          </Menu>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <IconButton size="small" onClick={(e) => setMoreMenuEl(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuEl}
            open={Boolean(moreMenuEl)}
            onClose={() => setMoreMenuEl(null)}
          >
            <Can
              role={user.profile}
              perform="contacts-page:deleteContact"
              yes={() => (
                <MenuItem
                  disabled={selectedContacts.length === 0}
                  onClick={() => {
                    setMoreMenuEl(null);
                    setActionType("delete");
                    setConfirmOpen(true);
                  }}
                >
                  {i18n.t("contacts.buttons.delete")}
                </MenuItem>
              )}
            />
            <MenuItem
              onClick={() => {
                setMoreMenuEl(null);
                if (selectedContacts.length === 0) {
                  toast.error(i18n.t("contacts.messages.selectToExport"));
                  return;
                }
                setActionType("export");
                setConfirmOpen(true);
              }}
            >
              {i18n.t("contacts.buttons.export")}
            </MenuItem>
          </Menu>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <>
          <input
              style={{ display: "none" }}
              id="upload"
              name="file"
              type="file"
              accept=".xls,.xlsx"
              onChange={() => {
                // ✅ CORREGIR: Configurar actionType para importar y limpiar selección de contactos
                // Esto evita que se confunda con la acción de borrar cuando hay contactos seleccionados
                setActionType("import");
                setSelectedContacts([]); // Limpiar selección para evitar confusión
                setSelectAll(false);
                setConfirmOpen(true);
              }}
              ref={fileUploadRef}
          />
        </>
        {viewMode === "table" ? (
        <Table size="small" className={classes.contactsTable}>
          <colgroup>
            <col style={{ width: 52 }} />
            <col />
            <col />
            <col />
            <col />
            <col style={{ width: 132 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" className={classes.checkboxCell}>
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                />
              </TableCell>
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell>{i18n.t("contacts.table.whatsapp")}</TableCell>
              <TableCell>{i18n.t("contacts.table.email")}</TableCell>
              <TableCell>{i18n.t("contacts.table.lastInteraction")}</TableCell>
              <TableCell align="right">{i18n.t("contacts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={`row-${contact.id}-${contact.profilePicUrl || 'no-pic'}-${contact.name}`} className={classes.tableRow} hover>
                  <TableCell padding="checkbox" className={classes.checkboxCell}>
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => handleSelectContact(contact.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <span className={classes.nameCell}>
                    <Avatar
                      className={`${classes.avatar}${!(contact.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png")) ? " avatar-no-photo" : ""}`}
                      style={{ backgroundColor: generateColor(contact?.number) }}
                      src={contact.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png") ? `${getProfilePicUrl(contact.profilePicUrl)}?v=${Date.now()}` : null}>
                      {getInitials(contact?.name)}
                    </Avatar>
                    <span className={classes.contactName}>{contact.name}</span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.mutedText}>{contact.number || "—"}</span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.mutedText}>{contact.email || "—"}</span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.mutedText}>{getDateLastMessage(contact) || "—"}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span className={classes.actionButtons}>
                    <IconButton
                      size="small"
                      className={classes.rowAction}
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.rowAction}
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          className={`${classes.rowAction} ${classes.dangerAction}`}
                          onClick={(e) => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={5} />}
            </>
          </TableBody>
        </Table>
        ) : (
          <div className={classes.cardsGrid}>
            {contacts.map((contact) => (
              <Paper key={`card-${contact.id}`} className={classes.contactCard} elevation={0}>
                <span className={classes.nameCell}>
                  <Checkbox
                    checked={selectedContacts.includes(contact.id)}
                    onChange={() => handleSelectContact(contact.id)}
                    color="primary"
                  />
                  <Avatar
                    className={`${classes.avatar}${!(contact.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png")) ? " avatar-no-photo" : ""}`}
                    style={{ backgroundColor: generateColor(contact?.number) }}
                    src={contact.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png") ? `${getProfilePicUrl(contact.profilePicUrl)}?v=${Date.now()}` : null}
                  >
                    {getInitials(contact?.name)}
                  </Avatar>
                  <span className={classes.contactName}>{contact.name}</span>
                </span>
                <div className={classes.contactCardMeta}>WhatsApp: {contact.number || "—"}</div>
                <div className={classes.contactCardMeta}>Correo electrónico: {contact.email || "—"}</div>
                <div className={classes.contactCardMeta}>
                  {Array.isArray(contact.tags) && contact.tags.length
                    ? contact.tags.map((tag) => tag.name || tag).filter(Boolean).join(", ")
                    : "Sin etiquetas"}
                </div>
                <div className={classes.contactCardFooter}>
                  <span className={classes.mutedText}>{getDateLastMessage(contact) || "—"}</span>
                  <span className={classes.actionButtons}>
                    <IconButton size="small" className={classes.rowAction} onClick={() => { setContactTicket(contact); setNewTicketModalOpen(true); }}>
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" className={classes.rowAction} onClick={() => hadleEditContact(contact.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton size="small" className={`${classes.rowAction} ${classes.dangerAction}`} onClick={() => { setConfirmOpen(true); setDeletingContact(contact); }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    />
                  </span>
                </div>
              </Paper>
            ))}
          </div>
        )}
        
        {!loading && totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 20, 30, 50, 100]}
            labelRowsPerPage={i18n.t("contacts.pagination.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              i18n.t("contacts.pagination.displayedRows", {
                from,
                to,
                count: count !== -1 ? count : to,
              })
            }
            style={{
              backgroundColor: 'transparent',
              color: 'inherit',
            }}
          />
        )}
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
